import React, { useEffect } from "react";
import classnames from "classnames";
import { useRouter } from "next/router";
import pluralize from "pluralize";
import { useDispatch } from "react-redux";

import { CATEGORY_NAMES } from "constants/dispensary";
import { Action, Category, Label } from "constants/events";
import { useEventTracker } from "hooks/useEventTracker";
import useFilters from "hooks/useFilters";
import { MAX_DISTANCE } from "hooks/useShop";
import { REQUEST_LOCATION } from "redux/reducers/location";

import Placeholder, { Rect } from "components/Placeholder";

const ShopResultsDescription: React.FC<{
  className?: string;
  loading?: boolean;
  showEmptyState?: boolean;
  totalCount: number;
}> = ({ className, loading, showEmptyState, totalCount }) => {
  const { publishEvent } = useEventTracker();
  const {
    query: { q, shopName, shopCategory, strain_name, deal_active, showSimilar },
    asPath,
  } = useRouter();
  const { selectFilterValue } = useFilters();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!totalCount) {
      publishEvent({
        action: Action.impression,
        category: Category.searchShopResults,
        label: Label.nullResults,
        nonInteractionEvent: true,
      });
    }
  }, [totalCount]);

  const showSimilarStrains =
    shopCategory === "strain" &&
    (showSimilar === "on" || showSimilar === "menu_items_only");

  const typeFilter = selectFilterValue("product_category") as string;
  const isStrainHub = asPath.includes("promoted-strains");

  const filterString =
    typeFilter && (CATEGORY_NAMES[typeFilter] || typeFilter).toLowerCase();

  const filteringOnBrand = shopName && shopCategory === "brand";
  const filteringOnDispensary = shopName && shopCategory === "dispensary";
  let detailString = "";
  if (filteringOnBrand) {
    detailString = "by";
  } else if (filteringOnDispensary) {
    detailString = "at";
  } else if (shopName) {
    detailString = "with";
  }

  const updateLocation = React.useCallback(() => {
    dispatch({ type: REQUEST_LOCATION });

    publishEvent({
      action: Action.click,
      category: Category.searchShopResults,
      label: "update location link - within 30 miles",
    });
  }, []);

  return loading ? (
    <Placeholder height="22" width="280">
      <Rect height="100%" width="100%" />
    </Placeholder>
  ) : (
    <h3
      className={classnames("heading--s font-normal", className)}
      data-testid="shop-results-description"
    >
      {showSimilarStrains ? (
        <div>
          {shopName && <b>&nbsp;{String(shopName)}</b>}
          <span
            role="button"
            tabIndex={0}
            className="text-green"
            onClick={updateLocation}
            onKeyPress={updateLocation}
            data-testid="update-location__link"
          >
            &nbsp;within {MAX_DISTANCE} miles.
          </span>
        </div>
      ) : (
        <div>
          <b>
            {showEmptyState && (
              <>
                <span className="font-normal">No</span>
                {deal_active ? " deal" : " product"}
              </>
            )}
            {!showEmptyState && totalCount.toLocaleString("en-us")}
            &nbsp;
            <span className={classnames({ "font-normal": showEmptyState })}>
              {pluralize(
                isStrainHub && !showEmptyState ? "product" : "result",
                totalCount,
              )}
            </span>
          </b>
          {(q || filterString) && <span>&nbsp;for</span>}
          {q && <b>&nbsp;"{String(q)}"</b>}
          {filterString && <b>&nbsp;{filterString}</b>}
          {detailString && <span>&nbsp;{detailString}</span>}
          {shopName && <b>&nbsp;{shopName}</b>}
          {showEmptyState && !filteringOnDispensary && !isStrainHub && (
            <span>&nbsp;on dispensary menus</span>
          )}
          {isStrainHub && strain_name && (
            <span>&nbsp;with {String(strain_name)}</span>
          )}
          <span
            role="button"
            tabIndex={0}
            className="text-green"
            onClick={updateLocation}
            onKeyPress={updateLocation}
            data-testid="update-location__link"
          >
            &nbsp;within {MAX_DISTANCE} miles.
          </span>
        </div>
      )}
    </h3>
  );
};

export default ShopResultsDescription;
